import { useContext, useMemo, useEffect, useState } from 'react';
import { CSSObject } from 'styled-components';
import reduce from 'lodash/reduce';
import Button, { IButtonProps } from '~/src/components/LandingBlock/Common/Button';
import { HeaderPanel } from './HeaderPanel';
import CallbackForm from './CallbackForm';
import { IPhoneForm } from './CallbackForm/types';
import { ImagePlaceholder } from '~/src/components/LandingBlock/styled';
import { IMG_DOMAIN } from '~/src/services/blocks/templates';
import { getPartialStyles } from '~/src/helpers/get-partial-styles';
import { ICityData } from '~/src/components/LandingBlock/Header/HeaderPanel';
import * as ST from './styled';
import { TAnalytics } from '~/src/services/blocks/configs/Part/analyticsPartConfig';
import { PortalElementContext } from '~/src/components/StickyElementForPortal/useStickyElementContext';
import loadReactDom from './loadReactDom';
import { isHtmlWithText } from '~/src/helpers/isHtmlWithText';
import { getTextWithTag } from '~/src/helpers/getTextWithTag';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';

interface IPictureProp {
    src: string;
    alt: string;
    webp: string;
}

export interface IAgeRestriction {
    active: boolean;
    backgroundColor?: string;
    image?: string;
    webp?: string;
}

export type IHeaderButtonProps = Pick<IButtonProps,
    'buttonText' |
    'href' |
    'idForScroll' |
    'isTargetBlank' |
    'analytics' |
    'backgroundColor' |
    'backgroundHoverColor' |
    'align'|
    'enableCalltouch'>

export interface IHeaderBlockProps {
    blockId: string,
    isAdmin: boolean,
    title: string;
    text: string;
    form?: IPhoneForm;
    analytics?: TAnalytics;
    picture?: IPictureProp;
    logo?: IPictureProp;
    buttonProps?: IHeaderButtonProps;
    subtitle?: string;
    customStyles?: ST.IHeaderBlockStyles;
    cityData: ICityData;
    background?: string;
    alias?: string;
    additionalBlockConfig?: any;
    ageRestriction?: IAgeRestriction;
    isAutoHeightSize?: boolean;
    direction?: 'vertical' | 'horizontal';
    titleTag?: string;
    subtitleTag?: string;
}

function StickyCallbackFormWithImportPortal (props) {
    const { stickyElement } = useContext(PortalElementContext);

    const [portalImport, setPortalImport] = useState(null);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        setPending(true);
        loadReactDom().then((createPortal) => {
            setPending(false);
            setPortalImport({ createPortal });
        });
    }, []);

    if (!portalImport?.createPortal || pending || !stickyElement) {
        return null;
    }

    return <>
        {portalImport.createPortal(<ST.StickyForm id={'sticky-form'}>
            <ST.Grid>
                <ST.RowGrow middle='xs'>
                    <ST.Col xs={12}>
                        <ST.Blur>
                            <svg fill='none' width='100%' height='100%' xmlns='http://www.w3.org/2000/svg'>
                                <rect width='100%' height='100%' fill='#fff' fillOpacity='.5'/>
                            </svg>
                        </ST.Blur>
                        <CallbackForm
                            gaLabel='banner'
                            {...props}
                        />
                    </ST.Col></ST.RowGrow>
            </ST.Grid>
        </ST.StickyForm>, stickyElement)}</>;
}

const HeaderBlock = ({
    blockId,
    isAdmin,
    title,
    subtitle,
    text,
    form,
    analytics,
    logo,
    buttonProps,
    picture,
    customStyles,
    cityData,
    background,
    alias,
    additionalBlockConfig,
    ageRestriction,
    isAutoHeightSize,
    direction = 'horizontal',
    titleTag,
    subtitleTag,
}: IHeaderBlockProps): JSX.Element => {
    const preparedCustomStyles = useMemo(() => {
        return reduce(customStyles, (acc, item, key) => {
            acc[key.replace(/Header[12]?/, 'Header')] = item;
            return acc;
        }, {});
    }, [customStyles]);

    const partialButtonStyles = useMemo(() => {
        return getPartialStyles(preparedCustomStyles as CSSObject, 'Button');
    }, [preparedCustomStyles]);

    const titleWithTag = getTextWithTag(title, titleTag);
    const subtitleWithTag = getTextWithTag(subtitle, subtitleTag);

    return (
        <>
            <ST.Wrapper
                id={blockId}
                {...preparedCustomStyles}
                background={background}
                isAutoHeightSize={isAutoHeightSize}
                {...additionalBlockConfig}
            >
                <ST.Grid modeName='wide'>
                <HeaderPanel
                    isAdmin={isAdmin}
                    logo={logo}
                    customStyles={preparedCustomStyles}
                    cityData={cityData}
                    ageRestriction={ageRestriction}
                />
                <ST.RowGrow middle='xs' direction={direction}>
                    <ST.Col xs={12}>
                        {subtitle && isHtmlWithText(subtitle) && (
                            <ST.SubTitle {...preparedCustomStyles} dangerouslySetInnerHTML={{ __html: subtitleWithTag }} />
                        )}
                        {isHtmlWithText(title) && (
                            <ST.Title {...preparedCustomStyles} dangerouslySetInnerHTML={{ __html: titleWithTag }} />
                        )}
                        {text &&
                            <ST.Text
                                {...preparedCustomStyles}
                                dangerouslySetInnerHTML={{ __html: text }}
                            />
                            }
                            {form && (
                                <CallbackForm
                                    form={form}
                                    isNeedHideForSticky={form.isBottomSticky}
                                    isAdmin={isAdmin}
                                    analytics={analytics}
                                    gaLabel='banner'
                                    customStyles={preparedCustomStyles}
                                    pageAlias={alias}
                                    direction={direction}
                                />
                            )}
                        {direction === 'horizontal' &&
                            <ST.BtnWrap>
                                <Button
                                    elemId={`${blockId}.button`}
                                    href={buttonProps.href}
                                    isTargetBlank={buttonProps.isTargetBlank}
                                    idForScroll={buttonProps.idForScroll}
                                    enableCalltouch={buttonProps.enableCalltouch}
                                    analytics={buttonProps.analytics}
                                    buttonText={buttonProps.buttonText}
                                    backgroundColor={buttonProps.backgroundColor}
                                    backgroundHoverColor={buttonProps.backgroundHoverColor}
                                    customStyles={partialButtonStyles}
                                />
                            </ST.BtnWrap>
                        }
                        </ST.Col>
                    </ST.RowGrow>

                    <ST.Image {...preparedCustomStyles} {...additionalBlockConfig} direction={direction}>
                        {picture?.src
                            ? <WebpImage urlWebp={picture.webp && `${IMG_DOMAIN}${picture.webp}`}>
                                <img src={`${IMG_DOMAIN}${picture.src}`} alt={picture.alt || title}/>
                            </WebpImage>
                            : <ImagePlaceholder width={250} height={200} isAdmin={isAdmin}/>
                        }
                    </ST.Image>
                </ST.Grid>
            </ST.Wrapper>
            {form && form?.isBottomSticky &&
            <StickyCallbackFormWithImportPortal
                form={form}
                isAdmin={isAdmin}
                analytics={analytics}
                gaLabel='banner'
                customStyles={preparedCustomStyles}
                pageAlias={alias}
            />}
        </>
    );
};

export default HeaderBlock;
